import Vue from "vue"
import Vuex, { StoreOptions } from "vuex"

import auth from "./auth.js"
import reduxi from "./reduxi.js"
import sidebar from "./sidebar.js"

import externalTokensModule, { ExternalTokensState } from "./external-tokens"
import externalDevicesModule, { ExternalDevicesState } from "./external-devices"
import adminSearchModule from "./admin-search"
import { AdminSearchState } from "@/types/admin-search"
import searchModule, { SearchState } from "./search-module"
import siteInfo, { SiteInfoState } from "@/store/site-info"
import toastMessageModule, { ToastMessageState } from "./toast-message"
import mojElektroCalculationsModule, {
    MojElektroCalculationsState,
} from "./moj-elektro/calculations"
import mojElektroFiltersModule, { MojElektroFiltersState } from "./moj-elektro/filters"
import appLayoutModule, { AppLayoutState } from "./app-layout"
import oemDeviceBackupsModule from "./oem-device-backups"
import { OemDeviceBackupsState } from "./oem-device-backups"
import oemDeviceControllersModule, { OemDeviceControllersState } from "./oem-device-controllers"
import setupGridModule from "./setup/setup-grid"

import { SetupGridState } from "./setup/setup-grid"
import setupModule, { SetupModuleState } from "./setup/index"
import navigationContextModule, { NavigationContextState } from "./navigation-context"
import breadcrumbsModule, { BreadcrumbsState } from "./breadcrumbs/breadcrumbs"
import { SidebarState } from "@/types/sidebar"
import locationSettingsModule, { LocationSettingsState } from "@/store/location-settings"
import timeOfUsePlansModule from "@/store/time-of-use/time-of-use"
import { TimeOfUseState } from "@/store/time-of-use/types"
import currenciesModule, { CurrenciesState } from "@/store/currencies"
import { PricingPlansState } from "@/store/pricing-plans/types"
import pricingPlansModule from "@/store/pricing-plans/pricing-plans"
import solarProductionModule from "./solar-production"
import { SolarProductionState } from "@/types/solar-production"
import savingsModule from "./savings"
import { SavingsState } from "@/types/savings"
import weatherModule from "./weather"
import { WeatherState } from "@/types/weather"
import themeCustomizerModule, { ThemeCustomizerState } from "./theme-customizer"
import locationChartsModule from "./location-charts"
import { LocationChartsState } from "./location-charts/types"
import evChargingModule from "./ev-charging"
import { EvChargingState } from "./ev-charging/types"
import locationModule, { LocationState } from "./location"
import alarmsModule from "./alarms/alarms"
import { AlarmsState } from "./alarms/types"
import twoFactorAuthModule from "./two-factor-auth/two-factor-auth"
import { TwoFactorAuthState } from "./two-factor-auth/types"
import alarmSettingsModule from "./alarm-settings/alarm-settings"
import { AlarmSettingsState } from "./alarm-settings/types"
import alarmNotificationsModule from "./alarm-notifications/alarm-notifications"
import { AlarmNotificationsState } from "./alarm-notifications/types"

Vue.use(Vuex)
export interface RootState {
    auth: any
    externalTokens: ExternalTokensState
    externalDevices: ExternalDevicesState
    adminSearchModule: AdminSearchState
    searchModule: SearchState
    toastMessageModule: ToastMessageState
    siteInfo: SiteInfoState
    mojElektroCalculationsModule: MojElektroCalculationsState
    mojElektroFiltersModule: MojElektroFiltersState
    appLayoutModule: AppLayoutState
    oemDeviceBackups: OemDeviceBackupsState
    oemDeviceControllers: OemDeviceControllersState
    setupGrid: SetupGridState
    setup: SetupModuleState
    navigationContext: NavigationContextState
    breadcrumbs: BreadcrumbsState
    sidebar: SidebarState
    locationSettings: LocationSettingsState
    timeOfUsePlans: TimeOfUseState
    solarProduction: SolarProductionState
    savings: SavingsState
    weather: WeatherState
    currencies: CurrenciesState
    pricingPlans: PricingPlansState
    themeCustomizer: ThemeCustomizerState
    locationCharts: LocationChartsState
    evCharging: EvChargingState
    location: LocationState
    alarms: AlarmsState
    twoFactorAuth: TwoFactorAuthState
    alarmSettings: AlarmSettingsState
    alarmNotifications: AlarmNotificationsState
}

const storeOptions: StoreOptions<RootState> = {
    plugins: [],
    modules: {
        //@ts-ignore: Ignoring typing for JavaScript modules
        auth,
        reduxi,
        sidebar,
        // Typed TypeScript modules
        externalTokens: externalTokensModule,
        externalDevices: externalDevicesModule,
        adminSearch: adminSearchModule,
        searchModule: searchModule,
        toastMessageModule: toastMessageModule,
        oemDeviceBackups: oemDeviceBackupsModule,
        oemDeviceControllers: oemDeviceControllersModule,
        siteInfo,
        mojElektroCalculationsModule,
        mojElektroFiltersModule,
        appLayoutModule,
        setupGrid: setupGridModule,
        setup: setupModule,
        navigationContext: navigationContextModule,
        breadcrumbs: breadcrumbsModule,
        locationSettings: locationSettingsModule,
        timeOfUsePlans: timeOfUsePlansModule,
        solarProduction: solarProductionModule,
        savings: savingsModule,
        weather: weatherModule,
        currencies: currenciesModule,
        pricingPlans: pricingPlansModule,
        themeCustomizer: themeCustomizerModule,
        locationCharts: locationChartsModule,
        evCharging: evChargingModule,
        location: locationModule,
        alarms: alarmsModule,
        twoFactorAuth: twoFactorAuthModule,
        alarmSettings: alarmSettingsModule,
        alarmNotifications: alarmNotificationsModule,
    },
    strict: process.env.NODE_ENV !== "production",
}

export default new Vuex.Store<RootState>(storeOptions)
